import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Translate } from '../../shared/LanguageService';
import RuleEditor from './RuleEditor';
import { RuleErrors } from './ruleValidation';
import { FeatureFlagRuleInput } from './types';

export interface FeatureFlagFormState {
    key: string;
    description: string;
    defaultEnabled: boolean;
    variants: string[];
    defaultVariant: string | null;
    rules: FeatureFlagRuleInput[];
}

interface FeatureFlagFormProps {
    state: FeatureFlagFormState;
    onChange: (state: FeatureFlagFormState) => void;
    isKeyEditable: boolean;
    keyError?: string | null;
    ruleErrors?: RuleErrors;
}

export default function FeatureFlagForm({
    state,
    onChange,
    isKeyEditable,
    keyError,
    ruleErrors
}: FeatureFlagFormProps) {
    const hasVariants = state.variants.length > 0;

    return (
        <Stack spacing={3}>
            <TextField
                label={Translate('feature-flags/form/key-label')}
                value={state.key}
                onChange={(e) => onChange({ ...state, key: e.target.value })}
                disabled={!isKeyEditable}
                required
                fullWidth
                error={Boolean(keyError)}
                helperText={
                    keyError != null
                        ? keyError
                        : Translate('feature-flags/form/key-helper')
                }
            />
            <TextField
                label={Translate('feature-flags/form/description-label')}
                value={state.description}
                onChange={(e) => onChange({ ...state, description: e.target.value })}
                fullWidth
                multiline
                minRows={2}
            />
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {Translate('feature-flags/form/all-users-label')}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    {hasVariants ? (
                        <FormControl size="small" sx={{ width: 170 }}>
                            <InputLabel id="feature-flag-default-variant-label">
                                {Translate('feature-flags/common/variant-label')}
                            </InputLabel>
                            <Select
                                labelId="feature-flag-default-variant-label"
                                label={Translate('feature-flags/common/variant-label')}
                                value={state.defaultVariant !== null ? state.defaultVariant : ''}
                                onChange={(e) =>
                                    onChange({ ...state, defaultVariant: e.target.value as string })
                                }
                            >
                                {state.variants.map((variant) => (
                                    <MenuItem key={variant} value={variant}>
                                        {variant}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Chip
                            clickable
                            color={state.defaultEnabled ? 'success' : 'default'}
                            label={
                                state.defaultEnabled
                                    ? Translate('feature-flags/common/enabled')
                                    : Translate('feature-flags/common/disabled')
                            }
                            onClick={() =>
                                onChange({ ...state, defaultEnabled: !state.defaultEnabled })
                            }
                            sx={{ minWidth: 110 }}
                        />
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {Translate('feature-flags/form/all-users-helper')}
                    </Typography>
                </Stack>
            </Box>
            <RuleEditor
                rules={state.rules}
                variants={state.variants}
                errors={ruleErrors}
                onChange={(rules) => onChange({ ...state, rules })}
            />
        </Stack>
    );
}
